import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { InputWithLabel, InputError } from "../../components/micro";
import { adminGetProfileData } from "../../redux/auth/slice";
import RippleLoader from "../../components/common/RippleLoader";
import Breadcrumb from "../../components/common/Breadcrumb";
import { Eye, EyeSlash, MailBox } from "../../assets/svg";

const Profile = () => {
  const [passwordType, setPasswordType] = useState("password");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getProfileData, profileLoading } = useSelector((store) => ({
    getProfileData: store?.authData?.adminGetProfileDataStatus?.data,
    profileLoading: store?.authData?.loading,
  }));

  useEffect(() => {
    setIsLoading(true);
    dispatch(adminGetProfileData({ navigate }));
    setIsLoading(false);
  }, [dispatch]);

  return (
    <>
      {profileLoading ? (
        <RippleLoader />
      ) : (
        <section>
          <p className="text-xl font-medium">Profile</p>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"My Profile"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
          <div className="mt-4">
            {/* User Profile */}
            <Formik
              enableReinitialize={true}
              initialValues={{
                full_name: getProfileData?.firstName ?? "",
                lastname: getProfileData?.lastName ?? "",
                email: getProfileData?.email ?? "",
                password: getProfileData?.password ?? "",
              }}
              validationSchema={Yup.object().shape({
                full_name: Yup.string().min(3).required("Please enter name"),
                lastname: Yup.string()
                  .required("Please enter last name")
                  .max(13, "Last name is too long."),
                email: Yup.string()
                  .email("Invalid email")
                  .required("Please enter e-mail"),
                password: getProfileData
                  ? Yup.string()
                      .min(
                        8,
                        "Password is too short - should be 8 characters minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      )
                  : Yup.string()
                      .required("Please enter password")
                      .min(
                        8,
                        "Password is too short - should be 8 characters minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      ),
              })}
              onSubmit={(values) => {}}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="w-full bg-bgWhite  shadow drop-shadow-md rounded-lg py-8 px-6 space-y-6 ">
                    {/* Name */}
                    <InputWithLabel
                      className={`${
                        isEditing === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border border-borderTheme/80 "
                      } transition duration-300 ease-in-out`}
                      idFromName="full_name"
                      label="Name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.full_name}
                      errors={errors.full_name && touched.full_name}
                      errorsText={errors.full_name}
                      disabled={!isEditing}
                    />
                    {/*Last Name */}
                    <InputWithLabel
                      className={`${
                        isEditing === "edit" && !isEditing
                          ? ""
                          : "text-black border "
                      }  transition duration-300 ease-in-out ${
                        !isEditing ? "bg-red" : "bg-transparent"
                      } rounded border-borderTheme/80 appearance-none peer`}
                      idFromName="lastname"
                      label="Last Name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastname}
                      errors={errors.lastname && touched.lastname}
                      errorsText={errors.lastname}
                      disabled={!isEditing}
                    />

                    {/* Email */}
                    <div>
                      <div
                        className={`${
                          errors.email && touched.email
                            ? `border border-red `
                            : `  `
                        } relative bg-white transition duration-300 rounded`}
                      >
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`block px-2.5 py-2 pl-11 w-full text-sm error-forms ${
                            isEditing === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black border"
                          } ${
                            !isEditing
                              ? "disabled:bg-zinc-300/20"
                              : "bg-transparent"
                          } rounded border-borderTheme/80 appearance-none disabled-opacity-80  peer`}
                          placeholder={"myemail@gmail.com"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isEditing}
                          value={values.email}
                        />
                        <MailBox className="absolute top-1/2 left-3 -translate-y-1/2" />
                        <label
                          htmlFor="email"
                          className="absolute top-0 left-1 -translate-y-2.5 text-light_grey pointer-events-none text-base scale-75 px-1 bg-white"
                        >
                          Email
                        </label>
                      </div>
                      {errors.email && touched.email ? (
                        <InputError errorTitle={errors.email} />
                      ) : null}
                    </div>
                    {/* Password */}
                    <div>
                      <div
                        className={`${
                          errors.email && touched.email
                            ? ` border border-red `
                            : `  `
                        } relative bg-white  transition duration-300 rounded`}
                      >
                        <input
                          type={passwordType}
                          id="password"
                          name="password"
                          className={`block px-2.5 py-2 pl-11 w-full text-sm error-forms ${
                            isEditing === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black border"
                          } ${
                            !isEditing
                              ? "disabled:bg-zinc-300/20"
                              : "bg-transparent"
                          } rounded border-borderTheme/80 appearance-none disabled-opacity-80  peer`}
                          placeholder={"Example@123"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          disabled={!isEditing}
                        />
                        <p
                          className="absolute top-1/2 left-3 -translate-y-1/2"
                          onClick={() => {
                            setPasswordType(
                              passwordType === "password" ? "text" : "password"
                            );
                          }}
                        >
                          {passwordType === "password" ? <EyeSlash /> : <Eye />}
                        </p>
                        <label
                          htmlFor="password"
                          className="absolute top-0 left-1 -translate-y-2.5 text-light_grey pointer-events-none text-base scale-75 px-1 bg-white"
                        >
                          Password
                        </label>
                      </div>
                      {errors.password && touched.password ? (
                        <InputError errorTitle={errors.password} />
                      ) : null}
                    </div>
                  </div>
                  {/* Buttons */}
                  <div className="flex justify-end mt-4">
                    <div
                      className="bg-themeBtn text-white flex items-center justify-center px-6 py-2.5 rounded uppercase font-Proxima cursor-pointer"
                      onClick={() => navigate("/")}
                    >
                      Back
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      )}
    </>
  );
};

export default Profile;
